import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface UserInfo {
    id?: number | string;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    role?: string;
    roles?: string[];
    profilePicture?: string;
}

export interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    role: string | undefined;
    user: UserInfo | null;
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    accessToken: null,
    refreshToken: null,
    role: undefined,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        loginSuccess: (state, action: PayloadAction<{ accessToken: string; refreshToken: string; role: string; user?: UserInfo }>) => {
            state.loading = false;
            state.isAuthenticated = true;
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.role = action.payload.role;
            state.user = action.payload.user || null;
            state.error = null;
        },
        loginFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.isAuthenticated = false;
            state.accessToken = null;
            state.refreshToken = null;
            state.role = undefined;
            state.user = null;
            state.error = action.payload;
        },
        logout: (state) => {
            state.accessToken = null;
            state.refreshToken = null;
            state.role = undefined;
            state.user = null;
            state.isAuthenticated = false;
            state.error = null;
        },
        updateUserProfile: (state, action: PayloadAction<Partial<UserInfo>>) => {
            if (state.user) {
                state.user = { ...state.user, ...action.payload };
            }
        },
        updateAccessToken: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const { 
    loginStart, 
    loginSuccess, 
    loginFailure, 
    logout, 
    updateAccessToken,
    updateUserProfile,
    clearError 
} = authSlice.actions;

export default authSlice.reducer;
